import React, { useRef, useImperativeHandle, forwardRef, useState, useEffect } from 'react';
import { AddIcon } from '../../../UI/Icons';
import { ValidationConstants } from '../../../../utils';
import { PositionSuggestionResponse } from '../../../../apiServices/types';
import PositionSuggestionsPanel from './PositionSuggestionsPanel';

interface AddItemFormProps {
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  categoryId?: string;
}

export interface AddItemFormRef {
  reset: () => void;
  focus: () => void;
}

const AddItemForm = forwardRef<AddItemFormRef, AddItemFormProps>(({ onSubmit, onKeyDown, categoryId }, ref) => {
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [itemName, setItemName] = useState<string>('');
  const [highlightedSuggestion, setHighlightedSuggestion] = useState<string | null>(null);
  const [inputStyles, setInputStyles] = useState<React.CSSProperties>({});

  useImperativeHandle(ref, () => ({
    reset: () => {
      formRef.current?.reset();
      setItemName('');
    },
    focus: () => {
      inputRef.current?.focus();
    }
  }));

  const handleSelectSuggestion = (value: string, shiftKey?: boolean) => {
    setItemName(value);
    // Встановити значення в інпут та відправити форму
    if (inputRef.current) {
      inputRef.current.value = value;
      const form = inputRef.current.form;
      if (form) {
        const formData = new FormData(form);
        formData.set('item-name', value);
        const syntheticEvent = {
          preventDefault: () => {},
          currentTarget: form,
          get: (name: string) => formData.get(name),
          shiftKey: shiftKey || false
        } as unknown as React.FormEvent<HTMLFormElement> & { shiftKey?: boolean };
        onSubmit(syntheticEvent);
        setItemName('');
        form.reset();
      }
    }
  };

  const handleHighlightChange = (suggestion: PositionSuggestionResponse | null) => {
    setHighlightedSuggestion(suggestion ? suggestion.name : null);
  };

  // Обчислити текст для оверлею (тільки частина після введеного тексту)
  const getOverlayText = () => {
    if (!highlightedSuggestion || !itemName || itemName.trim().length === 0) return null;
    const lowerItemName = itemName.toLowerCase().trim();
    const lowerSuggestion = highlightedSuggestion.toLowerCase();
    
    // Знайти позицію, з якої починається відмінність
    if (lowerSuggestion.startsWith(lowerItemName) && lowerItemName.length > 0) {
      return highlightedSuggestion.substring(itemName.trim().length);
    }
    return null;
  };

  const overlayText = getOverlayText();

  // Отримати стилі з інпута для точного збігу
  useEffect(() => {
    const updateStyles = () => {
      if (inputRef.current) {
        const computedStyle = window.getComputedStyle(inputRef.current);
        setInputStyles({
          fontFamily: computedStyle.fontFamily,
          fontSize: computedStyle.fontSize,
          fontStyle: computedStyle.fontStyle,
          fontWeight: computedStyle.fontWeight,
          letterSpacing: computedStyle.letterSpacing,
          lineHeight: computedStyle.lineHeight,
          padding: computedStyle.padding,
          paddingLeft: computedStyle.paddingLeft,
          paddingRight: computedStyle.paddingRight,
          paddingTop: computedStyle.paddingTop,
          paddingBottom: computedStyle.paddingBottom,
          borderLeftWidth: computedStyle.borderLeftWidth,
          borderRightWidth: computedStyle.borderRightWidth,
          borderTopWidth: computedStyle.borderTopWidth,
          borderBottomWidth: computedStyle.borderBottomWidth,
          height: computedStyle.height,
        });
      }
    };

    if (!inputRef.current) return;

    updateStyles();
    
    // Використати ResizeObserver для відстеження змін розміру
    const resizeObserver = new ResizeObserver(() => {
      updateStyles();
    });
    
    resizeObserver.observe(inputRef.current);
    
    // Оновити стилі при зміні розміру вікна
    window.addEventListener('resize', updateStyles);
    
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateStyles);
    };
  }, []);

  return (
    <form ref={formRef} className="add-item-form" onSubmit={onSubmit}>
      <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
        <input
          ref={inputRef}
          type="text"
          name="item-name"
          className="add-item-input"
          value={itemName}
          onChange={(e) => setItemName(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Додати товар"
          required
          minLength={ValidationConstants.ReceiptPosition.NameMinLength}
          maxLength={ValidationConstants.ReceiptPosition.NameMaxLength}
          autoComplete="off"
        />
        {overlayText && inputRef.current && Object.keys(inputStyles).length > 0 && (
          <span 
            className="add-item-input-overlay"
            style={{
              position: 'absolute',
              left: '2px',
              top: 0,
              pointerEvents: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              color: 'var(--primary)',
              opacity: 0.7,
              whiteSpace: 'nowrap',
              boxSizing: 'border-box',
              overflow: 'hidden',
              width: '100%',
              fontFamily: inputStyles.fontFamily,
              fontSize: inputStyles.fontSize,
              fontStyle: inputStyles.fontStyle,
              fontWeight: inputStyles.fontWeight,
              letterSpacing: inputStyles.letterSpacing,
              lineHeight: inputStyles.lineHeight,
              padding: inputStyles.padding,
              paddingLeft: inputStyles.paddingLeft,
              paddingRight: inputStyles.paddingRight,
              paddingTop: inputStyles.paddingTop,
              paddingBottom: inputStyles.paddingBottom,
              height: inputStyles.height,
            }}
          >
            <span style={{ visibility: 'hidden', whiteSpace: 'pre' }}>{itemName}</span>
            <span>{overlayText}</span>
          </span>
        )}
        <PositionSuggestionsPanel
          value={itemName}
          categoryId={categoryId}
          onSelect={handleSelectSuggestion}
          onHighlightChange={handleHighlightChange}
          inputRef={inputRef}
        />
      </div>
      <button type="submit" className="add-item-button">
        <AddIcon size={16} />
      </button>
    </form>
  );
});

AddItemForm.displayName = 'AddItemForm';

export default AddItemForm;
